<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

const props = defineProps({
  ticket: { type: Object, required: true },
  layout: {
    type: String,
    default: 'row',
    validator: v => ['row', 'card'].includes(v),
  },
});

defineEmits(['open']);

const { t } = useI18n();

// Solid status dot (matches the column dots in TicketsList).
const STATUS_DOT = {
  open: 'bg-n-slate-9',
  in_progress: 'bg-n-blue-9',
  escalated: 'bg-n-ruby-9',
  waiting_client: 'bg-n-amber-9',
  waiting_internal: 'bg-n-amber-9',
  resolved: 'bg-n-teal-9',
  closed: 'bg-n-slate-8',
  cancelled: 'bg-n-slate-8',
};

const PRIORITY_META = {
  urgent: 'text-n-ruby-9',
  high: 'text-n-amber-9',
  medium: 'text-n-blue-9',
  low: 'text-n-slate-9',
};

const statusDot = computed(
  () => STATUS_DOT[props.ticket.status] || STATUS_DOT.open
);
const statusLabel = computed(() =>
  t(`TICKETS.STATUS.${(props.ticket.status || 'open').toUpperCase()}`)
);
const priorityLabel = p => t(`TICKETS.PRIORITY.${p.toUpperCase()}`);

const isWaiting = computed(() =>
  ['waiting_client', 'waiting_internal'].includes(props.ticket.status)
);
const waitingLabel = computed(() => statusLabel.value);

// SLA acts as the "due date": red when breached or already past for a live ticket.
const isActiveStatus = computed(
  () => !['resolved', 'closed', 'cancelled'].includes(props.ticket.status)
);
const slaOverdue = computed(() => {
  if (props.ticket.sla_breached) return true;
  if (!props.ticket.sla_deadline || !isActiveStatus.value) return false;
  return Date.parse(props.ticket.sla_deadline) < Date.now();
});

const formatDate = d =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
      })
    : '';
const slaDate = computed(() => formatDate(props.ticket.sla_deadline));
const refLabel = computed(
  () => props.ticket.folio || `#${props.ticket.display_id}`
);
</script>

<template>
  <!-- Card layout (board) -->
  <div
    v-if="layout === 'card'"
    class="flex flex-col gap-2 p-3 transition-colors cursor-pointer rounded-xl outline-1 outline outline-n-weak bg-n-solid-1 hover:outline-n-slate-6"
    @click="$emit('open', ticket)"
  >
    <div class="flex items-start gap-2">
      <span
        v-tooltip.top="statusLabel"
        class="w-2 h-2 mt-1.5 rounded-full shrink-0"
        :class="statusDot"
      />
      <span class="flex-1 text-sm text-n-slate-12">{{ ticket.title }}</span>
    </div>
    <div class="flex items-center gap-2">
      <span class="font-mono text-xs text-n-slate-9">{{ refLabel }}</span>
      <span
        v-if="ticket.priority && ticket.priority !== 'medium'"
        v-tooltip.top="priorityLabel(ticket.priority)"
        class="i-lucide-flag"
        :class="PRIORITY_META[ticket.priority]"
      />
      <Badge v-if="ticket.status === 'escalated'" color="red" size="small">
        {{ t('TICKETS.STATUS.ESCALATED') }}
      </Badge>
      <span
        v-if="slaDate"
        class="ml-auto text-xs"
        :class="slaOverdue ? 'text-n-ruby-9 font-medium' : 'text-n-slate-10'"
      >
        {{ slaDate }}
      </span>
      <Avatar
        v-if="ticket.assignee"
        :name="ticket.assignee.name"
        :size="20"
        :class="slaDate ? '' : 'ml-auto'"
      />
    </div>
  </div>

  <!-- Row layout (list) -->
  <div
    v-else
    class="flex items-center gap-3 px-6 py-2.5 cursor-pointer hover:bg-n-alpha-1"
    @click="$emit('open', ticket)"
  >
    <span
      v-tooltip.top="statusLabel"
      class="w-2 h-2 rounded-full shrink-0"
      :class="statusDot"
    />

    <span class="font-mono text-xs shrink-0 text-n-slate-9">{{
      refLabel
    }}</span>

    <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
      {{ ticket.title }}
    </span>

    <!-- Variable badges sit left of the fixed columns -->
    <Badge v-if="ticket.status === 'escalated'" color="red" size="small">
      {{ t('TICKETS.STATUS.ESCALATED') }}
    </Badge>
    <Badge v-else-if="isWaiting" color="yellow" size="small">
      {{ waitingLabel }}
    </Badge>
    <Badge v-if="ticket.sla_breached" color="red" size="small">
      {{ t('TICKETS.BADGES.SLA_BREACHED') }}
    </Badge>

    <!-- Fixed-width columns: priority · SLA · assignee (always aligned) -->
    <span class="flex justify-center w-5 shrink-0">
      <span
        v-if="ticket.priority && ticket.priority !== 'medium'"
        v-tooltip.top="priorityLabel(ticket.priority)"
        class="i-lucide-flag"
        :class="PRIORITY_META[ticket.priority]"
      />
    </span>
    <span
      class="text-xs text-right shrink-0 w-14"
      :class="slaOverdue ? 'text-n-ruby-9 font-medium' : 'text-n-slate-10'"
    >
      {{ slaDate }}
    </span>
    <span class="flex justify-center w-6 shrink-0">
      <Avatar v-if="ticket.assignee" :name="ticket.assignee.name" :size="22" />
    </span>
  </div>
</template>
